// this is a grab bag of functionality that Storefront.Ruby used to do in
// react-redux-initializer.js

// on-initialization-actions.js
import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { shallowEqual, useDispatch, useSelector, useStore } from "react-redux";
import { parse as qsParse } from "qs";
import { useRouter } from "next/router";
import {
  fireRubyNextTrackingEvents,
  getCsrfToken,
  googleMapsLoaded,
  initializeFilters,
  initTatari,
  setPageTypeAndName,
} from "actions/on-initialization-actions";
import { useUser, UserSuspense } from "./UserContext";
import { useFeatureFlags } from "./FeatureFlagsContext";
import setupNavigation from "../../helpers/setup-navigation";
import analyticsClient from "../../analytics/analytics-client";
import { useCsrfProtection } from "../source/hoc/with-csrf-token";
import { initializeStoreForAjaxHelper } from "../../helpers/ajax-helpers";
import ActionLogger from "action-logger";
import { initLocationChangeEvent } from "../source/hoc/with-query-params";
import { refreshSegments } from "../../helpers/segment-refresh-helper";
import PhotoReviewsServiceClient from "clients/PhotoReviewsServiceClient.js";
import { executePostAuthenticationSideEffects } from "actions/post-authentication-actions";
import { DiscoSearchServiceClient } from "clients/disco-search-service-client";
import { useOnLoadFlagOrExperiment } from "hooks/use-flags-and-experiments";
import { flagsAndExperimentNames } from "rtr-constants";

const miscDataSelector = ({ gridView, product, publicEnv }) => ({ gridView, product, publicEnv });

const propTypes = {
  pageName: PropTypes.string,
  pageType: PropTypes.string,
};

export default function AppInitialization({ pageType, pageName }) {
  const dispatch = useDispatch();
  const router = useRouter();
  const { gridView, product, publicEnv } = useSelector(miscDataSelector, shallowEqual);
  const staticProduct = useSelector(state => state.staticProduct);
  // This doesn't work - 'user' needs to be destructured from useUser first and then you can grab the id. However,
  // we need to investigate whether we even want this code anymore, and if we do it needs to go through some vetting
  // first.
  const featureFlags = useFeatureFlags() || [];
  const csrfProtection = useCsrfProtection();
  const user = useUser();

  // ensure the SACT_686_HEAP_PIXEL_LOGGING flag is loaded on every page, so that
  // we can use it as a kill switch for heap pixel logging. I don't love putting this here
  // but it's a temporary measure until we can remove the flag and pixel code entirely.
  useOnLoadFlagOrExperiment(flagsAndExperimentNames.SACT_686_HEAP_PIXEL_LOGGING);

  useEffect(() => {
    if (!csrfProtection?.hasLoaded) dispatch(getCsrfToken());
  }, [csrfProtection?.hasLoaded]);

  // inspired by the _very_ old header.js
  const handleRouteChange = url => {
    analyticsClient.log({
      event: "page_load",
      object_type: "top_nav",
      context: url,
      referrer: ActionLogger.getReferrer(),
      uid: user?.user?.id,
    });
  };

  useEffect(() => {
    if (user?.hasLoaded) {
      handleRouteChange(window.location.href);
    }
  }, [user?.hasLoaded]);

  useEffect(() => {
    if (publicEnv?.gateWayURL) {
      // For client side rendering make sure add the gateWayURL as below
      PhotoReviewsServiceClient.setHost(publicEnv?.gateWayURL);
      DiscoSearchServiceClient.setHost(publicEnv?.gateWayURL);
    }
  }, [publicEnv?.gateWayURL]);

  useEffect(() => {
    // URL change handling
    initLocationChangeEvent();
  }, []);

  useEffect(() => {
    // gMapsLoaded callback
    // This callback is called when the Google Maps script loads. Until this point, we're not able to render
    // an autocomplete component. This function is called when that library is loaded and passes an event to
    // react which calls setState.
    window.gMapsLoaded = () => {
      dispatch(googleMapsLoaded());
    };
    // gMapsLoaded callback.

    if (featureFlags.tatari) {
      dispatch(initTatari());
    }
  }, [dispatch]);

  const queryString = router.asPath.includes("?") ? router.asPath.split("?")[1] : "";
  const queryObject = qsParse(queryString, { ignoreQueryPrefix: true });

  useEffect(() => {
    const existingQuery = {
      queryParams: queryObject,
      isPDP: typeof product === "object" || typeof staticProduct === "object",
    };

    //sbenedict 7/25 Next grids properly initialize filters in Redux serverside
    //Attempting to do so again with Ruby-specific logic was the source of several bugs
    //Keeping this block of code for other pages because I'm not sure about implications for the PDP
    if (typeof gridView !== "object") {
      dispatch(initializeFilters(existingQuery));
    }
  }, []);

  const store = useStore();

  useEffect(() => setupNavigation(store), [store]);
  useEffect(() => {
    initializeStoreForAjaxHelper(store);
  }, [store]);
  const handleUserData = user => {
    // `?? null` to prevent parameter defaults from kicking in
    const { id = null } = user || {};

    if (id) {
      refreshSegments();
      dispatch(executePostAuthenticationSideEffects());
    }
  };

  useEffect(() => {
    dispatch(setPageTypeAndName(pageType, pageName));
  }, [pageType, pageName]);

  useEffect(() => {
    dispatch(fireRubyNextTrackingEvents("next"));
  }, []);

  return <UserSuspense onInitialRender={handleUserData} />;
}
AppInitialization.propTypes = propTypes;
